<template>
  <section class="login h-full">
    <div
      class="flex flex-col items-center justify-center px-6 py-8 mx-auto h-full lg:py-0"
    >
      <div
        class="p-6 space-y-4 md:space-y-6 sm:p-8 flex items-center justify-center flex-col"
      >
        <!-- <brand-logo class="mb-6" /> -->
        <div
          class="w-full bg-white rounded-lg shadow md:mt-0 sm:max-w-md xl:p-0"
        >
          <component
            class="current-view"
            :is="componentMapper[currentConfig].component"
            :formData="formData"
            :errors="$v"
            :loadingStates="loadingStates"
            :loginStates="loginStates"
            :isNewClient="isNewClient"
            :linkIdentifier="linkIdentifier"
            @on-change="onChange($event)"
            @on-submit="onSubmit($event)"
          />
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import VueCookies from "vue-cookies";
import { mapActions, mapGetters, mapMutations } from "vuex";

const LoginEmail = () => import("../components/login-email");
const LoginOTP = () => import("../components/login-otp");
const LoginClient = () => import("../components/login-client");
const ForgotPassword = () => import("../components/forgot-password");
const ResetPassword = () => import("../components/reset-password");

//libs
import { required, email, sameAs, helpers } from "vuelidate/lib/validators";
import axios from "@/axios";
const passwordReg = helpers.regex(
  "alpha",
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&#])[A-Za-z\d@$!%*?&#]{12,}$/
);

export default {
  name: "AdditionalFormLogin",

  components: {
    LoginEmail,
    LoginOTP,
    LoginClient,
    ResetPassword,
  },

  validations: {
    formData: {
      email: {
        required,
        email,
      },
      otp: {
        required,
      },
      password: {
        required,
        passwordReg,
      },
      confirmPassword: { required, sameAsPassword: sameAs("password") },
    },
    validationGroup: ["formData.password", "formData.confirmPassword"],
  },

  data() {
    return {
      componentMapper: {
        loginEmail: { component: LoginEmail },
        loginOTP: { component: LoginOTP },
        loginClient: { component: LoginClient },
        forgotPassword: { component: ForgotPassword },
        resetPassword: { component: ResetPassword },
      },
      formData: {
        email: "",
        otp: "",
        clients: null,
        password: "",
        confirmPassword: "",
        session_id: "",
        request_id: "",
      },
      currentConfig: "loginEmail",
      loading: false, // global loader
      loadingStates: {
        otp: false,
        otpVerify: false,
        otpResend: false,
        resetPass: false,
        otpForgot: false,
        passLogin: false,
      },
      loginStates: {
        otpResend: false,
        counterValue: 30,
        clients: [],
      },
      isNewClient: false,
      byPassOtp: false,
    };
  },

  computed: {
    ...mapGetters({
      getValidatedData: "AdditionalFormsStore/getValidatedData"
    }),
    // in case we have a link_identifier param in URL we use another auth flow
    // * currently, linkIdentifier means that user will be redirected to the Additional Form Page
    // * linkIdentifier is the source for the getValidatedData
    linkIdentifier() {
      return this.$route.query.link_identifier || null;
    },
  },

  created() {
    const nextUrl = VueCookies.get("next-url");
    // there is no sense to redirect user to the same route:
    if (nextUrl === this.$route.fullPath) VueCookies.remove("next-url");

    // don't interrupt login to the additional form even if the client already has logged in:
    // const token = VueCookies.get("token");
    let token = VueCookies.get("tenant_token") || VueCookies.get("client_token");
    if (token && !this.linkIdentifier) {
      this.$router.push({ name: "dashboard.view" });
    }
  },

  async mounted() {
    if (this.linkIdentifier) {
      if (!this.getValidatedData) {
        // validate link_identifier if page opened with direct link
        try {
          await this.validateIdentifier(this.linkIdentifier);
        } catch {
          // remove invalid link_identifier from URL
          const query = { ...this.$route.query };
          delete query.link_identifier;
          this.$router.replace({
            ...this.$route,
            query,
          });
        }
      }
    }
    if (this.getValidatedData) {
      this.formData.email = this.getValidatedData.email;
      this.currentConfig = "loginEmail";
      // await this.verifyLinkIdentifier();
    }
  },
  methods: {
    ...mapActions({
      validateIdentifier: "AdditionalFormsStore/validateIdentifier",
      setLoginSession: "AdditionalFormsStore/setLoginSession"
    }),
    ...mapMutations({
      SET_ADDITIONAL_FORM_DATA: "AdditionalFormsStore/SET_ADDITIONAL_FORM_DATA",
    }),
    onChange({ value, type }) {
      this.formData[type] = value;
      // this.$v.formData[type].$touch();
    },
    async onSubmit({ component }) {
      // by pass the OPT Screen when there is only on client with sso login

      switch (component) {
        case "loginOTP":
          await this.onSendOTP(component, "otp");
          break;
        case "loginClient":
          this.byPassOtp ? " " : await this.onVerifyOTP(component);
          break;
        case "loginEmail":
          this.resetFormData();
          this.currentConfig = component;
          break;
        case "resendOTP":
          await this.onSendOTP(null, "otpResend");
          this.startCounter();
          break;
        case "forgotPassword":
          this.formData.otp = "";
          this.formData.password = "";
          await this.onReqResetPass(component, "otpForgot");
          break;
        case "resetState":
          // this.formData.otp = "";
          await this.onResetPassword("loginClient");
          break;
        case "case-dashboard":
          // this.currentConfig =
          await this.onPasswordLogin();
          break;

        default:
          this.currentConfig = component;
          break;
      }
    },
    resetFormData() {
      this.formData = {
        email: "",
        otp: "",
        clients: null,
        password: "",
        confirmPassword: "",
        session_id: "",
      };
    },
    async onSendOTP(component, type) {
      this.loadingStates[type] = true;
      try {
        if (this.getValidatedData) {
          await this.generateExtAuthOtp();
        } else {
          let response = await axios.post(`/client-auth/send-otp`, {
            email: this.formData.email,
          });
          let data = null;
          data =
            response?.data && Array.isArray(response.data)
              ? response.data[0]
              : response.data;
          this.byPassOtp = data?.bypass_otp || false;
          if (this.byPassOtp) {
            let client = [
              {
                id: data?.id,
                client_name: data?.client_name,
                tenant_title: data?.tenant_title,
                user_id: data?.user_id,
                is_new: data?.is_new,
                allow_external_auth: data?.allow_external_auth,
              },
            ];
            this.loginStates.clients = client ?? [];
            this.formData = { ...this.formData, clients: client[0] };
            if (!data?.allow_external_auth) {
              this.onSubmit({ component: "loginClient" });
              this.currentConfig = "loginClient" ?? this.currentConfig;
            } else {
              await this.fetchExternalUrl(data?.id);
            }
          } else {
            this.byPassOtp = false;
            this.formData = {
              ...this.formData,
              request_id: data.request_id,
              masked_no: data?.masked_no || null,
            };
            this.currentConfig = component ?? this.currentConfig;
          }
        }
      } catch (error) {
        // this.$toast.error("Failed to Send OTP, Please Try Again");
        this.$toast.error(
          "Failed to Send One-time Password, Please Try Again",
          {
            hideProgressBar: true,
          }
        );
        console.log(error, "[+] error while sending the OTP");
      }
      this.loadingStates[type] = false;
    },

    async fetchExternalUrl(selectedClientId) {
      try {
        const url = `client-auth/login`;
        const response = await axios.post(url, {
          client_id: selectedClientId,
        });
        if (response?.data?.auth_uri) {
          window.location.href = response?.data?.auth_uri;
        } else {
          this.$toast.error("link not found.");
        }
      } catch (err) {
        this.$toast.error(
          err?.response?.data?.detail || "something went wrong"
        );
      }
    },
    async onVerifyOTP(component) {
      this.loadingStates.otpVerify = true;
      try {
        if (this.getValidatedData) {
          await this.verifyExtAuthOTP();
        } else {
          const { email, otp, request_id } = this.formData;
          const { data } = await axios.post(
            `/client-auth/validate-otp`,
            {
              email,
              pin: otp,
              request_id,
            },
            {
              validateOtp: true,
            }
          );
          this.loginStates.clients = data?.clients ?? [];
          if (data?.clients?.length == 1) {
            this.formData.clients = data.clients[0];
          }
          this.currentConfig = component;
        }
      } catch (error) {
        this.$toast.error(
          error?.response?.data?.detail
            ? error?.response?.data?.detail
            : "Something Went wrong",
          {
            hideProgressBar: true,
          }
        );
      }
      this.loadingStates.otpVerify = false;
    },
    startCounter() {
      this.loginStates.otpResend = true;
      this.loginStates.counterValue = 30;
      const delay = 1000;
      const timer = setInterval(() => {
        this.loginStates.counterValue -= 1;
        if (this.loginStates.counterValue === 0) {
          clearInterval(timer);
          this.loginStates.otpResend = false;
        }
      }, delay);
    },
    async onResetPassword(component) {
      this.loadingStates.resetPass = true;
      try {
        const { email, otp, clients, password, session_id } = this.formData;
        if (this.isNewClient) {
          const { data } = await axios.post(
            `/client-auth/submit-new-password`,
            {
              new_password: password,
              session: session_id,
              user_id: clients.user_id,
              client_id: clients.id,
            }
          );
          this.loginUser(data);
        } else {
          await axios.post(`/client-auth/submit-reset-password`, {
            pin: otp,
            new_password: password,
            user_id: clients.user_id,
            client_id: clients.id,
            email,
          });
          this.currentConfig = component;
        }
      } catch (error) {
        console.log(error, "[+] error while resetting the password");
      }
      this.loadingStates.resetPass = false;
    },
    async onReqResetPass(component, type) {
      this.loadingStates[type] = true;
      try {
        await axios.post(`/client-auth/request-reset-password`, {
          client_id: this.formData.clients.id,
          user_id: this.formData.clients.user_id,
        });
        this.currentConfig = component;
      } catch (error) {
        this.$toast.error(
          "Failed to Send One-time Password, Please Try Again",
          {
            hideProgressBar: true,
          }
        );
        console.log(error, "[+] error while requesting the OTP");
      }
      this.loadingStates[type] = true;
    },
    async onPasswordLogin() {
      this.loadingStates.passLogin = true;
      try {
        const { email, clients, password } = this.formData;
        const { data } = await axios.post(`/client-auth/password-login`, {
          email,
          client_id: clients.id,
          password,
        });
        if (data.response_type === "new_password_required") {
          this.formData.password = "";
          this.formData.session_id = data.session;
          this.currentConfig = "resetPassword";
          this.isNewClient = true;
        } else {
          this.isNewClient = false;
          this.loginUser(data);
        }
      } catch (error) {
        this.$toast.error("Incorrect password, Please Try Again", {
          hideProgressBar: true,
        });
        console.log(error, "[+] error while login");
      }
      this.loadingStates.passLogin = false;
    },
    loginUser(userInfo = {}) {
      let user = {};
      let { user_id, user_name, case_entity_id, additional = false } = userInfo;
      user = { user_id, email, user_name };

      this.setLoginSession({
        token: userInfo?.access_token,
        refresh_token: userInfo?.refresh_token,
        userLoggedIn: true,
        user,
        additional,
      });
      if (this.getValidatedData) {
        this.$router.push({
          name: "form-delvium-additional-form",
          query: { link_identifier: this.linkIdentifier, case_entity_id },
        });
      } else {
        const nextUrl = VueCookies.get("next-url");
        if (nextUrl && nextUrl != "/logged-out/") {
          this.$router.push(nextUrl);
          // clear next-url after redirect
          VueCookies.remove("next-url");
        } else {
          try {
            this.$nextTick(() => {
              this.$router.push({ name: "dashboard.view" });
            });
          } catch (err) {
            console.error("Route not found err", err);
          }
          // this.$router
          //     .push({
          //         name: "dashboard.view",
          //     })
          //     .catch((e) => e);
        }
      }
    },
    async generateExtAuthOtp() {
      const { user_id, data_collection_id, form_id } = this.getValidatedData;
      const payload = {
        user_id,
        data_collection_id,
        form_id,
        link_identifier: this.linkIdentifier,
      };

      const { data: generateOtpResponse } = await axios.post(
        "/ext-auth/generate-otp",
        payload
      );
      const { email, session } = generateOtpResponse;
      this.formData.email = email;
      this.formData.session_id = session;
      if (this.linkIdentifier) {
        this.currentConfig = "loginOTP";
      }
    },
    async verifyLinkIdentifier() {
      this.loadingStates.otp = true;
      try {
        this.formData.email = this.getValidatedData.email;
        await this.generateExtAuthOtp();
        // this.currentConfig = "loginOTP";
      } catch (error) {
        console.log("Failed to verify link", error);
      }
      this.loadingStates.otp = false;
    },
    async verifyExtAuthOTP() {
      const { user_id } = this.getValidatedData;
      const payload = {
        user_id,
        link_identifier: this.linkIdentifier,
        session: this.formData.session_id,
        secret_code: this.formData.otp.trim(),
      };
      const { data } = await axios.post("ext-auth/submit-otp", payload, {
        validateOtp: true,
      });

      if (data.success) {
        const userInfo = {
          user_id: this.getValidatedData.user_id,
          additional: true,
          ...data,
        };
        // Store form_type from ext-auth/submit-otp response
        if (data.form_type) {
          this.$store.commit('AdditionalFormsStore/SET_VALIDATED_DATA', {
            ...this.getValidatedData,
            form_type: data.form_type
          });
        }
        this.loginUser(userInfo);
        this.SET_ADDITIONAL_FORM_DATA(data);
      } else {
        this.$toast.error(data?.detail || "Invalid OTP");
        this.formData.session_id = data.session;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.login {
  background: #4d545c;
}

::v-deep {
  .otp:disabled {
    background-color: #a8a8a8 !important;
    cursor: none !important;
  }
  .loader {
    height: 1.5rem;
    width: 1.5rem;
    border-color: inherit;
    border-width: 2px !important;
  }
  .multiselect__tags {
    font-weight: 500 !important;
  }
  .dropdown {
    border-radius: 0.5rem !important;
  }
}
</style>
